"use client";

import { LoyaltyAccount } from "@/generated/email_parser.openapi";
import {
  AdditionalRateResponseV3,
  EarningsSummary,
  HotelDetails,
  HotelDetailsV3,
  HotelReviewItem,
  RawRateSummary,
  Review,
  RoomSummaryV3,
} from "@/generated/search.openapi";

import {
  Dispatch,
  MutableRefObject,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useContext,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import { searchAndBookTestEnv, handleError } from "@/lib/apiUtils";
import { getDateFormat } from "@/lib/utils";
import {
  HotelRateValidationPostRequest,
  RateType,
  RateValidationResponse,
  RateValidationResponseV2,
  RateValidationStatus,
  V2HotelHotelIdBookPostRequest,
  HotelBookingResponse,
  HotelBookingResponseStatus,
} from "@/generated/search.openapi";
import { usePostHog } from "posthog-js/react";
import { useGlobalStore } from "@/app/context/globalStoreProvider";
import { getCustomerIp, sanitizeLoyaltyId } from "@/lib/booking_utils";
import { sanitizeName } from "@/lib/booking/nameUtils";
import { canRenderThreeDsChallenge } from "@/lib/threeDsChallenge";
import { preserveSelectedRateType } from "@/lib/booking/rateTypeUtils";

export const DEFAULT_BOOKING_ERROR_MESSAGE =
  "Our apologies, we encountered an error while booking your stay. Please try again. Contact support@gondola.ai and our team will help you out if the problem persists.";

export const TEMPORARY_BOOKING_ERROR_MESSAGE =
  "Our apologies, we are experiencing a partial outage with one of our providers.  We are hoping to have this resolved in the next couple of hours.  Please contact concierge@gondola.ai if there's anything our team can help with immediately.";

export const CVV_ERROR_MESSAGES = {
  DEFAULT:
    "We couldn't verify your card's security code. Please check your card details and try again. If the problem persists, please contact support@gondola.ai.",
  INCOMPLETE_DATA: "Payment method data is incomplete. Please try again.",
  TIMEOUT: "The request timed out. Please try again.",
} as const;

export type ContactInfo = {
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber?: string;
  loyaltyId?: string;
  loyaltyStatus?: string;
};

export type BookingInfo = {
  checkinStr: string;
  checkoutStr: string;
  numberOfAdults: number;
  childrenAges: number[];
  room: RoomSummaryV3;
  rate: RawRateSummary;
  bookingToken: string;
  bookingId?: string;
  earningsSummary?: EarningsSummary;
};

type ReviewDetails = {
  review: (HotelReviewItem | Review)[];
};

type BookingContextType = {
  hotelDetails?: HotelDetailsV3 | HotelDetails;
  setHotelDetails?: Dispatch<
    SetStateAction<HotelDetailsV3 | HotelDetails | undefined>
  >;
  bookingInfo?: BookingInfo;
  setBookingInfo?: Dispatch<SetStateAction<BookingInfo | undefined>>;
  selectedRateForValidationRef?: MutableRefObject<RawRateSummary | undefined>;
  bookingInfoLoading?: boolean;
  setBookingInfoLoading?: Dispatch<SetStateAction<boolean>>;
  reviewDetails?: ReviewDetails;
  setReviewDetails?: Dispatch<SetStateAction<ReviewDetails | undefined>>;
  selectedLoyaltyAccount?: LoyaltyAccount;
  setSelectedLoyaltyAccount?: Dispatch<
    SetStateAction<LoyaltyAccount | undefined>
  >;
  rateResponse?: AdditionalRateResponseV3;
  setRateResponse?: Dispatch<
    SetStateAction<AdditionalRateResponseV3 | undefined>
  >;
};

const BookingContext = createContext<BookingContextType>({});

export default function BookingProvider({ children }: PropsWithChildren) {
  const [hotelDetails, setHotelDetails] = useState<
    HotelDetailsV3 | HotelDetails
  >();
  const [bookingInfo, setBookingInfo] = useState<BookingInfo>();
  const selectedRateForValidationRef = useRef<RawRateSummary | undefined>(
    undefined
  );
  const [reviewDetails, setReviewDetails] = useState<ReviewDetails>();
  const [bookingInfoLoading, setBookingInfoLoading] = useState<boolean>(false);
  const [selectedLoyaltyAccount, setSelectedLoyaltyAccount] =
    useState<LoyaltyAccount>();
  const [rateResponse, setRateResponse] = useState<AdditionalRateResponseV3>();
  return (
    <BookingContext.Provider
      value={{
        hotelDetails,
        setHotelDetails,
        bookingInfo,
        setBookingInfo,
        selectedRateForValidationRef,
        reviewDetails,
        setReviewDetails,
        bookingInfoLoading,
        setBookingInfoLoading,
        selectedLoyaltyAccount,
        setSelectedLoyaltyAccount,
        rateResponse,
        setRateResponse,
      }}
    >
      {children}
    </BookingContext.Provider>
  );
}

const useBooking = () => {
  const posthog = usePostHog();
  const { refetchDataPostBooking } = useGlobalStore();

  const {
    hotelDetails,
    setHotelDetails,
    bookingInfo,
    setBookingInfo,
    selectedRateForValidationRef,
    reviewDetails,
    setReviewDetails,
    bookingInfoLoading,
    setBookingInfoLoading,
    selectedLoyaltyAccount,
    setSelectedLoyaltyAccount,
    rateResponse,
    setRateResponse,
  } = useContext(BookingContext);

  const fetchPriceCheckV2 = async (loyaltyId?: string) => {
    const rate = bookingInfo?.rate;
    const room = bookingInfo?.room;
    const childrenAges = bookingInfo?.childrenAges || [];

    if (!bookingInfo || !rate || !room || !hotelDetails) {
      throw new Error("Missing required booking information");
    }

    // Prefer V2 endpoint when gondolaRateId is available
    if (rate.gondolaRateId) {
      const v2Response = (await fetch("/api/hotel/rateValidation/v2", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          gondolaRateId: rate.gondolaRateId,
          includeRoomInfo: true,
          localizedCurrency:
            rate.rateType === RateType.GondolaCash ? "USD" : undefined,
        }),
      }).then((res) => res.json())) as RateValidationResponseV2;

      if (
        v2Response.updateStatus === RateValidationStatus.RateNotFound ||
        v2Response.updateStatus === RateValidationStatus.RequestFailure
      ) {
        throw new Error("Rate is no longer available");
      }

      const validatedRateSummary =
        v2Response.updatedRawRateSummary?.rawRateSummary ||
        v2Response.originalRawRateSummary?.rawRateSummary;
      const rateSummary = validatedRateSummary
        ? {
            ...validatedRateSummary,
            rateType: preserveSelectedRateType(
              rate.rateType,
              validatedRateSummary.rateType
            ),
            inventoryRateType:
              rate.rateType === RateType.GondolaCash
                ? validatedRateSummary.rateType
                : validatedRateSummary.inventoryRateType,
          }
        : undefined;
      const earningsSummary =
        v2Response.updatedRawRateSummary?.earningsSummary ||
        v2Response.originalRawRateSummary?.earningsSummary;
      const bookingToken =
        rateSummary?.bookingToken || rateSummary?.rateId || "";

      return {
        rateSummary,
        earningsSummary,
        bookingToken,
        vervotechPropertyId: v2Response.vervotechPropertyId,
        roomSummary: v2Response.roomSummary,
      } as RateValidationResponse;
    }

    // Fall back to V1 when gondolaRateId is not available
    // Safely convert roomId - only include if valid number
    const roomIdNum =
      room.roomId && room.roomId !== "None" ? Number(room.roomId) : undefined;
    const roomId = roomIdNum && !isNaN(roomIdNum) ? roomIdNum : undefined;

    const request: HotelRateValidationPostRequest = {
      rateValidationRequest: {
        checkin: getDateFormat(bookingInfo.checkinStr as string),
        checkinStr: bookingInfo.checkinStr,
        checkout: getDateFormat(bookingInfo.checkoutStr as string),
        checkoutStr: bookingInfo.checkoutStr,
        rateId: rate.rateId as string,
        rateType: rate.rateType as RateType,
        vervotechPropertyId: hotelDetails.hotelSummary
          .vervotechPropertyId as number,
        rateToken: rate.rateToken as string,
        ...(roomId !== undefined && { roomId }),
        occupancy: `${bookingInfo.numberOfAdults as number}${childrenAges.length ? "-" + childrenAges.join(",") : ""}`,
        loyaltyAccountId: sanitizeLoyaltyId(loyaltyId),
        test: searchAndBookTestEnv,
      },
    };

    const response = await fetch("/api/hotel/priceCheck/v2", {
      method: "POST",
      body: JSON.stringify(request),
    });

    const roomRate = (await response.json()) as RateValidationResponse;
    return roomRate;
  };

  const refetchPriceCheck = async (loyaltyId?: string) => {
    try {
      setBookingInfoLoading?.(true);
      const v2Response = await fetchPriceCheckV2(loyaltyId);

      if (v2Response.rateSummary && v2Response.bookingToken) {
        const rate = v2Response.rateSummary as RawRateSummary;
        setBookingInfo?.((prev) => ({
          ...(prev as BookingInfo),
          rate: {
            ...rate,
            cancellationPolicyFull:
              rate.rateType === RateType.DirectCash ||
              rate.rateType === RateType.DirectPoints
                ? rate.cancellationPolicyFull
                : rate.cancellationPolicyFull,
          },
          bookingToken: v2Response.bookingToken!,
        }));
      } else {
        throw new Error("Error while booking, please try again later.");
      }
    } catch (error) {
      toast.error("Failed to load rate. Please try again.");
    } finally {
      setBookingInfoLoading?.(false);
    }
  };

  const bookThroughAPI = async (
    contactInfo: ContactInfo,
    paymentMethodId: string,
    useGondolaCredits: boolean,
    specialRequest?: string,
    loyaltyId?: string,
    bookingRequestId?: string
  ): Promise<{ bookingId: string }> => {
    if (!bookingInfo || !hotelDetails) {
      throw new Error(TEMPORARY_BOOKING_ERROR_MESSAGE);
    }

    try {
      const ip = (await getCustomerIp()) || "";
      const vervotechPropertyId =
        hotelDetails.hotelSummary.vervotechPropertyId?.toString() || "";

      const bookingRequest: V2HotelHotelIdBookPostRequest = {
        hotelId: vervotechPropertyId,
        bookingRequest: {
          loyaltyAccountId: sanitizeLoyaltyId(loyaltyId),
          test: searchAndBookTestEnv,
          specialRequest,
          bookingToken: bookingInfo.bookingToken ?? "",
          customerIp: ip,
          emailAddress: contactInfo.emailAddress,
          givenName: sanitizeName(contactInfo.firstName),
          familyName: sanitizeName(contactInfo.lastName),
          phoneNumber: contactInfo.phoneNumber ?? "",
          paymentId: useGondolaCredits ? "" : paymentMethodId,
          rateType: bookingInfo.rate.rateType as RateType,
          useAvailableCredits: useGondolaCredits,
          bookingRequestId,
          // GON-6638: this client can render a Stripe 3DS challenge. When the book-time
          // gate needs one, the refusal carries a SetupIntent client_secret instead of
          // dead-ending, and we complete it + retry (see lib/threeDsChallenge).
          supportsThreeDsChallenge: canRenderThreeDsChallenge(),
        },
      };

      const response = await fetch("/api/hotel/book", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(bookingRequest),
      });

      const bookingResponse = (await response.json()) as HotelBookingResponse;

      if (bookingResponse.statusCode !== 200) {
        // Create error with status information for proper error handling
        const error = new Error(
          bookingResponse.error?.message ?? TEMPORARY_BOOKING_ERROR_MESSAGE
        ) as Error & {
          status?: HotelBookingResponseStatus;
          threeDsClientSecret?: string;
        };
        error.status = bookingResponse.status;
        // GON-6638: a CARD_DECLINED refused pending a renderable 3DS challenge carries the
        // SetupIntent client_secret. Carry it through both throw sites — dropping it here is
        // what turns a completable challenge back into a dead-end decline.
        error.threeDsClientSecret =
          bookingResponse.threeDsClientSecret ?? undefined;
        throw error;
      }

      if (!bookingResponse.booking?.gondolaBookingId) {
        throw new Error(
          "Booking was successful but no booking ID was returned"
        );
      }

      refetchDataPostBooking();
      return {
        bookingId: bookingResponse.booking.gondolaBookingId,
      };
    } catch (error) {
      // GON-6638: a challenge-pending refusal is a funnel step, not a failure — the user
      // completes the bank challenge and retries. Counting it as "Booking failed" would
      // inflate the failure funnel by exactly the volume of the new feature, and would
      // disagree with the Sentry suppression below.
      const threeDsPending = Boolean(
        (error as { threeDsClientSecret?: string })?.threeDsClientSecret
      );
      posthog.capture(
        threeDsPending ? "Booking 3DS challenge required" : "Booking failed",
        { error: (error as Error)?.message ?? "Unknown error" }
      );
      throw error;
    }
  };

  const handleBookingSubmission = async (
    contactInfo: ContactInfo,
    paymentMethodId: string,
    useGondolaCredits: boolean,
    specialRequest?: string,
    loyaltyId?: string,
    bookingRequestId?: string
  ) => {
    try {
      // Submit booking
      const { bookingId } = await bookThroughAPI(
        contactInfo,
        paymentMethodId,
        useGondolaCredits,
        specialRequest,
        loyaltyId,
        bookingRequestId
      );

      if (!bookingId) {
        throw new Error(TEMPORARY_BOOKING_ERROR_MESSAGE);
      }
      return bookingId;
    } catch (error) {
      // Get user-friendly error message based on status
      const getErrorMessage = (
        status?: HotelBookingResponseStatus,
        originalMessage?: string
      ): string => {
        if (status) {
          switch (status) {
            case HotelBookingResponseStatus.RateNoLongerAvailable:
              return "This rate is no longer available. Please select a different rate.";
            case HotelBookingResponseStatus.RoomsUnavailable:
              return "Rooms are no longer available for these dates. Please try different dates or select another hotel.";
            case HotelBookingResponseStatus.CardDeclined:
              return "Your card was declined. Please try a different payment method.";
            case HotelBookingResponseStatus.AvailableCreditInsufficient:
              return "You don't have enough Gondola Cash to cover the full cost of this reservation. Please select a different payment method.";
            case HotelBookingResponseStatus.PriceMismatch:
              return "The price has changed. Please try again.";
            case HotelBookingResponseStatus.LoyaltyInfoError:
              return "There was an issue with your loyalty account. Please check your loyalty information and try again.";
            case HotelBookingResponseStatus.ServiceBusy:
              return "The booking service is temporarily busy. Please try again in a moment.";
            case HotelBookingResponseStatus.GondolaPaymentMethodNotAvailable:
              return "Your payment method is not available. Please select a different payment method.";
            case HotelBookingResponseStatus.BookingNotFound:
              return "Booking not found. Please try again.";
            case HotelBookingResponseStatus.InvalidFamilyNameLength:
              return "Family name must be at least 2 characters long. Please check your input and try again.";
            case HotelBookingResponseStatus.Unknown:
            default:
              return originalMessage || DEFAULT_BOOKING_ERROR_MESSAGE;
          }
        }
        return (
          originalMessage ||
          (error instanceof Error
            ? error.message
            : TEMPORARY_BOOKING_ERROR_MESSAGE)
        );
      };

      // Extract error message from the original error
      const errorWithStatus = error as Error & {
        status?: HotelBookingResponseStatus;
        threeDsClientSecret?: string;
      };
      const errorMessage = getErrorMessage(
        errorWithStatus.status,
        error instanceof Error ? error.message : undefined
      );

      // GON-6638: a challenge-pending decline is recoverable — the caller completes it and
      // retries, so this first refusal isn't worth paging on. The TERMINAL outcomes (dismissed,
      // Stripe unavailable, retry failed) are reported by the consumer instead, so the cohort
      // stays visible. Scoped to CARD_DECLINED to match the consumer's guard exactly.
      const isChallengePending =
        errorWithStatus.status === HotelBookingResponseStatus.CardDeclined &&
        Boolean(errorWithStatus.threeDsClientSecret);
      if (!isChallengePending) {
        // Report error to Sentry (silent - don't show toast here, let onSubmit handle it)
        handleError(errorMessage, {
          reportError: true,
          silent: true,
        });
      }

      // Throw with consistent structure that onSubmit expects
      // Include status so onSubmit can decide whether to navigate back, and the 3DS secret
      // so it can render the challenge instead of showing the decline copy.
      throw {
        errorMessage,
        status: errorWithStatus.status,
        threeDsClientSecret: errorWithStatus.threeDsClientSecret,
      };
    }
  };

  return {
    hotelDetails,
    setHotelDetails,
    bookingInfo,
    setBookingInfo,
    selectedRateForValidationRef,
    reviewDetails,
    setReviewDetails,
    bookingInfoLoading,
    setBookingInfoLoading,
    selectedLoyaltyAccount,
    setSelectedLoyaltyAccount,
    rateResponse,
    setRateResponse,
    fetchPriceCheckV2,
    refetchPriceCheck,
    handleBookingSubmission,
  };
};

export { useBooking };
